/**
 * F-148 AC12: duplicate variant rows block review until combined. Shows each group with the
 * summed preview (from the shared `consolidateLines`) before the merchant confirms.
 */
import { Merge } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { DuplicateGroup } from '../lib/draft-reducer';
import { withRawValues } from '../lib/error-messages';

export interface DuplicateVariantBannerProps {
  readonly groups: readonly DuplicateGroup[];
  readonly onCombine: (variantId: string) => void;
  readonly disabled?: boolean;
  readonly className?: string;
}

function fmt(value: number | null): string {
  return value === null ? '—' : String(value);
}

export function DuplicateVariantBanner({
  groups,
  onCombine,
  disabled = false,
  className,
}: DuplicateVariantBannerProps): React.JSX.Element | null {
  const { t } = useTranslation();
  if (groups.length === 0) return null;

  return (
    <section
      role="alert"
      data-testid="duplicate-variant-banner"
      className={cn(
        'rounded-lg border border-amber-200 bg-amber-50 p-4 text-start text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-100',
        className,
      )}
    >
      <h2 className="text-sm font-semibold">{t('admin.receiving.duplicates.title')}</h2>
      <ul className="mt-2 space-y-3">
        {groups.map((group) => (
          <li
            key={group.variantId}
            className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
            data-testid="duplicate-group"
            data-variant-id={group.variantId}
          >
            <div className="space-y-1 text-sm">
              <p>
                <bdi>
                  {t(
                    'admin.receiving.duplicates.description',
                    withRawValues({
                      product: group.productName,
                      variant: group.optionSummary,
                      lines: group.lineNos.join(', '),
                    }),
                  )}
                </bdi>
              </p>
              <p className="text-xs" data-testid="duplicate-preview">
                {t('admin.receiving.draft.combinePreview', {
                  expected: fmt(group.preview.expected),
                  accepted: fmt(group.preview.accepted),
                  damaged: fmt(group.preview.damaged),
                  missing: fmt(group.preview.missing),
                })}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              className="min-h-11 shrink-0"
              disabled={disabled}
              onClick={() => onCombine(group.variantId)}
              data-testid="duplicate-combine"
            >
              <Merge aria-hidden="true" className="size-4" />
              {t('admin.receiving.duplicates.combine')}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
